import { Component, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TextAreaComponent, TextInputComponent } from '@sandbox-platform/shared-ui';

import { PendingCommissionDTO } from '../../../../types/commission-invoice.types';

@Component({
  standalone: true,
  imports: [FormsModule, TextInputComponent, TextAreaComponent],
  selector: 'lib-commission-invoice-commissions-step',
  template: `
    <div class="flex flex-col gap-6">
      <section class="rounded-lg border border-gray-200 overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <p class="text-sm text-gray-600">
            Select commissions for <span class="font-medium">{{ payeeName() }}</span>
          </p>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 text-sm">
            <thead class="bg-slate-600 text-slate-100">
              <tr>
                <th class="px-4 py-3 w-8"></th>
                <th class="px-4 py-3 text-left font-medium">Referral</th>
                <th class="px-4 py-3 text-left font-medium">Type</th>
                <th class="px-4 py-3 text-left font-medium">Party</th>
                <th class="px-4 py-3 text-left font-medium">Recipient</th>
                <th class="px-4 py-3 text-left font-medium">Seq</th>
                <th class="px-4 py-3 text-left font-medium">Eligible From</th>
                <th class="px-4 py-3 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              @for (commission of commissions(); track commission.commission_id) {
              <tr
                class="cursor-pointer transition-colors"
                [class]="isSelected(commission) ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'"
                (click)="toggleCommission.emit(commission)"
              >
                <td class="px-4 py-3">
                  <input
                    type="checkbox"
                    [checked]="isSelected(commission)"
                    class="rounded text-orange-600 focus:ring-orange-500"
                    (click)="$event.stopPropagation()"
                    (change)="toggleCommission.emit(commission)"
                  />
                </td>
                <td class="px-4 py-3 font-mono text-xs text-gray-700">{{ commission.referral_code }}</td>
                <td class="px-4 py-3 text-gray-600">{{ titleCase(commission.commission_type) }}</td>
                <td class="px-4 py-3 text-gray-600">{{ titleCase(commission.commission_party) }}</td>
                <td class="px-4 py-3 text-gray-600">{{ commission.invoice_recipient_name || '-' }}</td>
                <td class="px-4 py-3 text-gray-600">{{ commission.sequence_number }}</td>
                <td class="px-4 py-3 text-gray-500 text-xs">{{ formatDate(commission.invoice_eligible_at) }}</td>
                <td class="px-4 py-3 text-right font-medium text-gray-900">
                  {{ formatMoney(commission.amount_minor, commission.currency) }}
                </td>
              </tr>
              } @empty {
              <tr>
                <td colspan="8" class="px-4 py-8 text-center text-sm text-gray-400">
                  No pending commissions found for this payee.
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        @if (commissions().length > 0) {
        <div class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <p class="text-sm text-gray-600">
            <span class="font-medium">{{ selectedKeys().size }}</span> selected
          </p>
          <p class="text-sm font-semibold text-gray-900">
            Total: {{ formatMoney(selectedTotal(), commissions()[0]?.currency ?? 'KES') }}
          </p>
        </div>
        }
      </section>

      <section class="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">Invoice Details</p>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <sb-text-input
            id="period-start"
            label="Period Start"
            type="date"
            [ngModel]="periodStart()"
            (ngModelChange)="periodStart.set($event)"
          />
          <sb-text-input
            id="period-end"
            label="Period End"
            type="date"
            [ngModel]="periodEnd()"
            (ngModelChange)="periodEnd.set($event)"
          />
          <sb-text-input
            id="due-date"
            label="Due Date"
            type="date"
            [ngModel]="dueDate()"
            (ngModelChange)="dueDate.set($event)"
          />
        </div>

        <sb-text-area
          id="invoice-notes"
          label="Notes"
          [rows]="2"
          placeholder="Add any notes for this invoice..."
          [ngModel]="notes()"
          (ngModelChange)="notes.set($event)"
        />
      </section>
    </div>
  `,
})
export class CommissionInvoiceCommissionsStepComponent {
  commissions = input.required<PendingCommissionDTO[]>();
  payeeName = input<string>('');
  selectedKeys = input.required<Set<string>>();
  selectedTotal = input<number>(0);

  periodStart = model<string>('');
  periodEnd = model<string>('');
  dueDate = model<string>('');
  notes = model<string>('');

  toggleCommission = output<PendingCommissionDTO>();

  protected isSelected(commission: PendingCommissionDTO): boolean {
    return this.selectedKeys().has(commission.commission_id);
  }

  protected formatMoney(amountMinor: number, currency = 'KES'): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amountMinor / 100);
  }

  protected formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  protected titleCase(value?: string): string {
    if (!value) return '-';
    return value
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1));
  }
}
